/**
 * Delays the execution of function that follow in the queue for provided time period.
 *
 * @param {number} millisecondDelay
 * @return {Promise}
 */
export function delay(millisecondDelay) {
  return new Promise(resolve => {
    setTimeout(resolve, millisecondDelay);
  });
}

/**
 * Counts the difference between two dates in days. If second parameter is not set, func uses current date.
 *
 * @param {number} dateTo
 * @param {number} dateFrom
 * @return {number}
 */
export function daysCounter(dateTo, dateFrom = 0) {
  const oneDay = 24 * 60 * 60 * 1000; // hours*minutes*seconds*milliseconds
  const from = dateFrom > 0 ? dateFrom : new Date();
  const to = new Date(dateTo);

  // set time to midnight to correctly calculate difference
  from.setHours(0, 0, 0, 0);
  to.setHours(0, 0, 0, 0);

  return Math.round(Math.abs((from.getTime() - to.getTime()) / oneDay));
}

/**
 * Check if date is older than one month from today.
 *
 * @param {string} dateFrom
 * @returns {boolean}
 */
export function isOlderThanOneMonth(dateFrom) {
  const targetDate = new Date(dateFrom);
  const oneMonthAgo = new Date();
  oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);

  return targetDate < oneMonthAgo;
}

/**
 * Creates and returns a new debounced version of the passed function which will postpone its execution
 * until after wait milliseconds have elapsed since the last time it was invoked.
 *
 * @param {function} func - callback function called after the debounce
 * @param {number} debounceDelay - how long after the action has finished we want to wait before callback function is called
 * @return {function}
 */
export function debounce(func, debounceDelay) {
  if (typeof func !== 'function') {
    throw new TypeError(`Expected a function as 1st param, got ${typeof func}`);
  }

  if (typeof debounceDelay !== 'number') {
    throw new TypeError(`Expected a number as 2nd param, got ${typeof debounceDelay}`);
  }

  let timeout;

  /* eslint-disable-next-line func-names */
  return function (...args) {
    const functionCall = () => func.apply(this, args);

    clearTimeout(timeout);
    timeout = setTimeout(functionCall, debounceDelay);
  };
}

/**
 * Creates and returns a new throttled version of the passed function
 *
 * @param {function} func - callback function that has to be throttled
 * @param {number} throttleDelay - in ms, how long after the action is executed we have to wait for enabling calling throttled function again
 * @return {function} | undefined
 */
export function throttle(func, throttleDelay) {
  if (typeof func !== 'function') {
    throw new TypeError(`Expected a function as 1st param, got ${typeof func}`);
  }

  if (typeof throttleDelay !== 'number') {
    throw new TypeError(`Expected a number as 2nd param, got ${typeof throttleDelay}`);
  }

  let lastCall = 0;

  /* eslint-disable-next-line func-names */
  return function (...args) {
    const now = new Date().getTime();
    if (now - lastCall < throttleDelay) {
      return undefined;
    }
    lastCall = now;
    return func.apply(this, args);
  };
}

/**
 *  Convert slovak accents (diacritics) from strings to latin characters.
 *
 * @param {string} inputString
 * @returns {string}
 */
export function removeDiacritics(inputString) {
  if (typeof inputString !== 'string') {
    throw new TypeError('Expected a string as 1st param');
  }

  const accentedCharacters = 'áäčďéíľĺňóôŕšťúýžÁÄČĎÉÍĽĹŇÓÔŔŠŤÚÝŽ';
  const latinCharacters = 'aacdeillnoorstuyzAACDEILLNOORSTUYZ';
  let trimmedInputSting = inputString.trim();

  Array.from(accentedCharacters).forEach((char, index) => {
    trimmedInputSting = trimmedInputSting.replace(
      new RegExp(char, 'g'),
      latinCharacters.charAt(index),
    );
  });

  return trimmedInputSting;
}

/**
 * Return object with values from url query.
 * Parsed by array parameterList
 *
 * @param {object} query - URLSearchParams
 * @param {object[]} parameterList
 * @return {object}
 */
export function getDataFromQuery(query, parameterList) {
  return parameterList.reduce((urlData, param) => {
    const data = { ...urlData };
    data[param] = query.getAll(param);
    return data;
  }, {});
}

/**
 * Truncates provided string to at the last word boundary
 * under the provided `maxLength`.
 *
 * @param {string} inputString - String ought to be truncated
 * @param {number} maxLength - Max length to which string will be truncated
 * @returns {string}
 */
export function truncateString(inputString, maxLength = 140) {
  if (typeof inputString !== 'string') {
    throw new TypeError(`Expected a string as 1st param, got ${typeof inputString}`);
  }

  if (inputString.length <= maxLength) {
    return inputString;
  }

  const subString = inputString.substr(0, maxLength - 3);
  const end = subString.lastIndexOf(' ') >= 0 ? subString.lastIndexOf(' ') : subString.length;

  return `${inputString.slice(0, end)}...`;
}

/**
 * Removes HTML tags from provided string.
 * Note that this method is not 100 percent secure and may strip also equality symbols.
 *
 * @see https://github.com/esamattis/underscore.string
 * @param {string} inputString - String of which tags ought to be removed
 * @returns {string}
 */
export function stripTags(inputString) {
  if (typeof inputString !== 'string') {
    throw new TypeError(`Expected a string as 1st param, got ${typeof inputString}`);
  }

  return inputString.replace(/<\/?[^>]+>/g, '');
}

/**
 * Rounds provided number to 2 decimal places.
 * `Number.EPSILON` handles edge cases like number 1.005 and such that would be rounded incorrectly by JS.
 *
 * @see https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Number/EPSILON
 * @see https://stackoverflow.com/questions/11832914/how-to-round-to-at-most-2-decimal-places-if-necessary#answer-41716722
 * @param {number} num
 * @returns {number}
 */
export function roundTo2Decimals(num) {
  if (typeof num !== 'number') {
    throw new TypeError(`Expected a number as a param, got ${typeof num}`);
  }

  return Math.round((num + Number.EPSILON) * 100) / 100;
}

/**
 * Calculates and returns tax and revenue from provided price.
 * Default tax percentage is a constant of 20%.
 *
 * @param {number} price
 * @param {number} taxPercentage
 * @returns {{revenue: number, tax: number}}
 */
export function getTaxAndRevenue(price, taxPercentage = 23) {
  if (typeof price !== 'number') {
    throw new TypeError(`Expected a number as a param, got ${typeof price}`);
  }

  const tax = (price / (100 + taxPercentage)) * taxPercentage;
  const roundedTax = roundTo2Decimals(tax);
  const revenue = price - roundedTax;
  return {
    revenue,
    tax: roundedTax,
  };
}

/**
 * Convert array of objects into CSV
 *
 * @param {array} jsonArrayToConvert
 * @param {array} header
 * @param {string} delimiter
 * @returns {string}
 */
export function convertJsonToCsv(jsonArrayToConvert, header, delimiter = ',') {
  if (!Array.isArray(jsonArrayToConvert)) {
    throw new TypeError(`Expected array of objects, got ${typeof jsonArrayToConvert}`);
  }

  if (!Array.isArray(header)) {
    throw new TypeError(`Expected array of objects, got ${typeof header}`);
  }

  if (typeof delimiter !== 'string') {
    throw new TypeError(`Expected a string as 3th param, got ${typeof delimiter}`);
  }

  const replacer = (_, value) => (value === null ? '' : value);
  const csv = jsonArrayToConvert.map(row =>
    header.map(field => JSON.stringify(row[field.columnKey], replacer)).join(delimiter),
  );
  csv.unshift(header.map(field => JSON.stringify(field.columnTitle)).join(delimiter));

  return csv.join('\r\n');
}

/**
 * Checks whether provided string is valid according to ISO8601.
 * Currently doesn’t support the time interval or duration part of the ISO 8601 spec.
 *
 * @see https://www.myintervals.com/blog/2009/05/20/iso-8601-date-validation-that-doesnt-suck/
 * @param {string} inputString
 * @returns {boolean}
 */
export function isValidISO8601Format(inputString) {
  if (typeof inputString !== 'string') {
    throw new TypeError(`Expected a string as 1st param, got ${typeof inputString}`);
  }

  return /^([+-]?\d{4}(?!\d{2}\b))((-?)((0[1-9]|1[0-2])(\3([12]\d|0[1-9]|3[01]))?|W([0-4]\d|5[0-2])(-?[1-7])?|(00[1-9]|0[1-9]\d|[12]\d{2}|3([0-5]\d|6[1-6])))([T\s]((([01]\d|2[0-3])((:?)[0-5]\d)?|24:?00)([.,]\d+(?!:))?)?(\17[0-5]\d([.,]\d+)?)?([zZ]|([+-])([01]\d|2[0-3]):?([0-5]\d)?)?)?)?$/.test(
    inputString,
  );
}

/**
 * Utility function that scrolls to Y axis of provided HTML element.
 *
 * There is a delay before the scroll itself to prevent a bug, when component
 * doing the scrolling is rendered, but it's children (e.g. accordion) are not
 * yet rendered and therefore document doesn't have proper height yet.
 *
 * @param {object} element
 * @param {string} behavior
 */
export async function scrollToElement(element, behavior = 'smooth') {
  if (!element?.offsetTop) {
    return;
  }
  let top = element.offsetTop;

  const { paddingTop } = document.body.style;
  if (paddingTop) {
    top -= parseInt(paddingTop, 10);
  }

  await delay(100);
  window.scroll({
    behavior,
    left: 0,
    top,
  });
}

/**
 * Checks whether provided url is relative.
 *
 * @param {string} url
 * @returns {boolean}
 */
export function isRelativeUrl(url) {
  if (typeof url !== 'string') {
    throw new TypeError(`Expected a string as 1st param, got ${typeof url}`);
  }
  return /^\/[^/]/g.test(url);
}

/**
 * Calculate the first day of the following billing period, given the current one.
 *
 * @param {{from: string, to: string}} currentBillingPeriod - `from` and `to` are both ISO date strings
 * @return {string} - ISO date string
 */
export function calculateNextBillingPeriodStartDate(currentBillingPeriod) {
  const periodEndDate = new Date(currentBillingPeriod.to);
  const billDay = periodEndDate.setDate(periodEndDate.getDate() + 1);
  return new Date(billDay).toISOString();
}

/**
 * Construct a correct full path _with_ a base path from a possibly dirty inputs.
 * Considers that `path` parameter might or might not include base path already.
 * Considers inconsistent leading and trailing slashes both `path` and `basePath` parameters.
 *
 * TODO: consider writing a solid test suite
 *
 * @param {string} path
 * @param {string} basePath
 * @param {boolean} withBasePath
 * @return {string}
 */
export function constructPath(path, basePath, withBasePath = true) {
  if (path === '/' && basePath === '/') {
    return basePath;
  }

  const preparedBasePath = basePath
    .replace(/^(\/*)([^/])/, '/$2') // assure single leading slash
    .replace(/\/+$/, ''); // assure no trailing slash

  const truePath = path
    .replace(/^(\/*)([^/])/, '/$2') // assure single leading slash
    .replace(/\/+$/, '') // assure no trailing slash
    .replace(preparedBasePath, ''); // remove base path if present

  return withBasePath ? `${preparedBasePath}${truePath}` : truePath;
}
